import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, RotateCcw, Search, MapPin, Clock, Plus, CalendarClock, LogIn, PackageOpen,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import SeoHead from "../components/SeoHead";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useServices } from "../context/ServicesContext";

const prettyStatus = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || "Received";

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const { services } = useServices();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkRef, setLinkRef] = useState("");
  const [linkPhone, setLinkPhone] = useState("");
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/bookings");
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) load();
    else if (!authLoading && !user) setLoading(false);
  }, [authLoading, user, load]);

  const rebook = (b) => {
    const svc =
      services.find((s) => s.id === b.category_id) ||
      services.find((s) => s.serviceId === b.service_id) ||
      services.find((s) => (s.name || "").toLowerCase() === (b.service_name || "").toLowerCase());
    navigate("/book", {
      state: {
        prefill: {
          service: svc || null,
          service_name: b.service_name,
          locality: b.locality || "",
          address: b.address || "",
          pincode: b.pincode || "",
          landmark: b.landmark || "",
          problem: b.problem_description || "",
        },
      },
    });
  };

  const linkBooking = async (e) => {
    e.preventDefault();
    if (!linkRef.trim() || !linkPhone.trim()) {
      toast.error("Enter Booking ID and mobile number");
      return;
    }
    setLinking(true);
    try {
      await api.post("/account/bookings/link", { ref: linkRef.trim(), phone: linkPhone.trim() });
      toast.success("Booking added to your account");
      setLinkRef("");
      setLinkPhone("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not find that booking");
    } finally {
      setLinking(false);
    }
  };

  if (!authLoading && !user) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-20 text-center">
        <SeoHead title="My Bookings - ApnaFix" />
        <span className="grid place-items-center w-16 h-16 rounded-full bg-orange-50 text-brand-orange mx-auto">
          <PackageOpen className="w-8 h-8" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Your bookings, in one place</h1>
        <p className="mt-2 text-slate-600">Sign in to see every service you've booked and re-book your favourites in one tap.</p>
        <Button data-testid="mybookings-signin-button" onClick={login} className="mt-6 h-11 px-6 bg-navy hover:bg-navy-900 text-white font-semibold">
          <LogIn className="w-4 h-4 mr-1.5" /> Sign in with Google
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="mybookings-page">
      <SeoHead title="My Bookings - ApnaFix" description="View and re-book your ApnaFix home-service bookings." />
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">My Bookings</h1>
      <p className="mt-2 text-slate-600">All the services you've booked while signed in. Tap "Book again" to reorder.</p>

      {/* Add older booking */}
      <form onSubmit={linkBooking} className="mt-6 bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Plus className="w-4 h-4 text-brand-orange" /> Add an older booking
        </div>
        <p className="text-xs text-slate-500 mt-1">Booked before signing in? Add it with your Booking ID and mobile number.</p>
        <div className="mt-3 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-sm">Booking ID</Label>
            <Input data-testid="mybookings-link-ref" value={linkRef} onChange={(e) => setLinkRef(e.target.value.toUpperCase())} placeholder="e.g. AF1959" className="mt-1.5 h-11 font-mono-num" />
          </div>
          <div>
            <Label className="text-sm">Mobile number</Label>
            <Input data-testid="mybookings-link-phone" value={linkPhone} onChange={(e) => setLinkPhone(e.target.value)} placeholder="Registered mobile" className="mt-1.5 h-11" />
          </div>
          <Button type="submit" data-testid="mybookings-link-submit" disabled={linking} className="h-11 bg-navy hover:bg-navy-900 text-white font-semibold">
            {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
      </form>

      {/* List */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : bookings.length === 0 ? (
          <div data-testid="mybookings-empty" className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
            <span className="grid place-items-center w-14 h-14 rounded-full bg-slate-100 text-slate-400 mx-auto"><CalendarClock className="w-7 h-7" /></span>
            <p className="mt-4 font-semibold text-slate-900">No bookings yet</p>
            <p className="text-sm text-slate-500 mt-1">Book a service and it'll show up here automatically.</p>
            <Button data-testid="mybookings-book-cta" onClick={() => navigate("/book")} className="mt-5 h-11 px-6 bg-brand-orange hover:bg-orange-600 text-white font-semibold">Book a Service</Button>
          </div>
        ) : (
          bookings.map((b) => (
            <div key={b.ref} data-testid={`mybooking-card-${b.ref}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono-num text-xs font-bold text-slate-400">#{b.ref}</span>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{prettyStatus(b.status)}</Badge>
                  {b.linked && <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-[10px]">Added</Badge>}
                </div>
                <h2 className="font-display text-lg font-bold text-slate-900 truncate">{b.service_name || "Service"}</h2>
                {(b.locality || b.preferred_slot) && (
                  <p className="text-sm text-slate-500 inline-flex items-center gap-1 flex-wrap">
                    {b.locality && <><MapPin className="w-3.5 h-3.5" /> {b.locality}</>}
                    {b.locality && b.preferred_slot ? " · " : ""}
                    {b.preferred_slot && <><Clock className="w-3.5 h-3.5" /> {b.preferred_slot}</>}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`mybooking-track-${b.ref}`} variant="outline" onClick={() => navigate(`/track/${b.ref}`)} className="h-10 font-semibold">
                  <Search className="w-4 h-4 mr-1" /> Track
                </Button>
                <Button data-testid={`mybooking-rebook-${b.ref}`} onClick={() => rebook(b)} className="h-10 bg-brand-orange hover:bg-orange-600 text-white font-semibold">
                  <RotateCcw className="w-4 h-4 mr-1" /> Book again
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
